import type { CandidateLocation, LocationBrief, StoreMetric } from "./types";

export interface ForecastResult {
  ready: boolean;
  gaps: string[];
  p10: number | null;
  p50: number | null;
  p90: number | null;
  ownCash: number | null;
  annualOccupancyCost: number | null;
  occupancyCostPct: number | null;
  comparableAverage: number | null;
  comparableNames: string[];
}

type ForecastSource = Pick<CandidateLocation,
  | "stage" | "site_address" | "eircode" | "latitude" | "longitude" | "location_confirmed_at"
  | "proposed_format" | "annual_rent" | "floor_area_sqft" | "fitout_estimate"
  | "comparable_store_keys" | "trading_days_per_week" | "trading_hours_per_week"
  | "counter_orders_per_day" | "average_counter_check" | "delivery_revenue_share"
  | "downside_adjustment_pct" | "upside_adjustment_pct" | "annual_rates"
  | "annual_service_charge" | "landlord_contribution" | "seating_capacity"
  | "peak_orders_per_hour" | "forecast_notes"
  | "context_notes" | "location_source"
>;

const positive = (value: number | null | undefined) => typeof value === "number" && Number.isFinite(value) && value > 0;
const present = (value: number | null | undefined) => typeof value === "number" && Number.isFinite(value) && value >= 0;

export function forecastReadiness(source: ForecastSource) {
  const gaps: string[] = [];
  if (source.stage !== "unit") gaps.push("Advance the case to a specific premises");
  if (!source.site_address || !source.eircode || source.latitude === null || source.longitude === null || !source.location_confirmed_at) gaps.push("Confirm the exact premises and map pin");
  if (/specific (retail )?unit[^.]{0,100}(tbc|remain|not yet)|unit tbc/i.test([source.proposed_format, source.context_notes, source.location_source].filter(Boolean).join(" "))) gaps.push("Confirm the specific retail unit, not only the shopping centre");
  if (!source.proposed_format) gaps.push("Select the proposed store format");
  if (!positive(source.floor_area_sqft)) gaps.push("Enter the unit floor area");
  if (!positive(source.annual_rent)) gaps.push("Enter the annual rent");
  if (!present(source.annual_rates)) gaps.push("Enter annual commercial rates, using zero only when confirmed");
  if (!present(source.annual_service_charge)) gaps.push("Enter the annual service charge, using zero only when confirmed");
  if (!positive(source.fitout_estimate)) gaps.push("Enter the fit-out estimate");
  if (!source.comparable_store_keys?.length) gaps.push("Select at least one comparable NAMO store");
  if (!positive(source.trading_days_per_week)) gaps.push("Enter planned trading days per week");
  if (!positive(source.trading_hours_per_week)) gaps.push("Enter planned trading hours per week");
  if (!positive(source.counter_orders_per_day)) gaps.push("Enter the base daily counter and collection order assumption");
  if (!positive(source.average_counter_check)) gaps.push("Enter the average counter and collection check");
  if (!present(source.delivery_revenue_share) || Number(source.delivery_revenue_share) >= 90) gaps.push("Enter a delivery revenue share below 90%");
  if (!positive(source.seating_capacity)) gaps.push("Enter the proposed seating capacity");
  if (!positive(source.peak_orders_per_hour)) gaps.push("Enter the practical peak order capacity");
  if (!source.forecast_notes?.trim()) gaps.push("Explain the forecast assumptions and their source");
  return gaps;
}

export function calculateForecast(source: ForecastSource, stores: StoreMetric[]): ForecastResult {
  const gaps = forecastReadiness(source);
  const selected = stores.filter((store) => !store.isProvisional && source.comparable_store_keys?.includes(store.storeId));
  if (source.comparable_store_keys?.length && !selected.length) gaps.push("Choose a mature store with an available performance benchmark");
  const comparableAverage = selected.length ? selected.reduce((sum, store) => sum + store.monthlyRunRate, 0) / selected.length : null;
  const comparableNames = selected.map((store) => store.name);
  if (gaps.length) return { ready: false, gaps: [...new Set(gaps)], p10: null, p50: null, p90: null, ownCash: null, annualOccupancyCost: null, occupancyCostPct: null, comparableAverage, comparableNames };

  const counterMonthly = Number(source.counter_orders_per_day) * Number(source.average_counter_check) * Number(source.trading_days_per_week) * 52 / 12;
  const p50 = counterMonthly / (1 - Number(source.delivery_revenue_share) / 100);
  const p10 = p50 * (1 - Number(source.downside_adjustment_pct ?? 15) / 100);
  const p90 = p50 * (1 + Number(source.upside_adjustment_pct ?? 15) / 100);
  const annualOccupancyCost = Number(source.annual_rent) + Number(source.annual_rates) + Number(source.annual_service_charge);
  const occupancyCostPct = annualOccupancyCost / (p50 * 12) * 100;
  const ownCash = Math.max(0, Number(source.fitout_estimate) - Number(source.landlord_contribution ?? 0));
  return { ready: true, gaps: [], p10, p50, p90, ownCash, annualOccupancyCost, occupancyCostPct, comparableAverage, comparableNames };
}

export function forecastFieldsFromBrief(brief: LocationBrief) {
  const toNumber = (value: string) => value === "" ? null : Number(value);
  return {
    comparable_store_keys: brief.comparable_store_keys,
    trading_days_per_week: toNumber(brief.trading_days_per_week),
    trading_hours_per_week: toNumber(brief.trading_hours_per_week),
    counter_orders_per_day: toNumber(brief.counter_orders_per_day),
    average_counter_check: toNumber(brief.average_counter_check),
    delivery_revenue_share: toNumber(brief.delivery_revenue_share),
    downside_adjustment_pct: Number(brief.downside_adjustment_pct || 15),
    upside_adjustment_pct: Number(brief.upside_adjustment_pct || 15),
    annual_rates: toNumber(brief.annual_rates),
    annual_service_charge: toNumber(brief.annual_service_charge),
    landlord_contribution: toNumber(brief.landlord_contribution),
    rent_free_months: toNumber(brief.rent_free_months),
    seating_capacity: toNumber(brief.seating_capacity),
    peak_orders_per_hour: toNumber(brief.peak_orders_per_hour),
    centre_monthly_footfall: toNumber(brief.centre_monthly_footfall),
    footfall_source: brief.footfall_source.trim() || null,
    forecast_notes: brief.forecast_notes.trim() || null,
  };
}
